import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { services } from '../../home/home.services';
import './Performance.scss';

const Performance = ({ showSidebar }) => {
    const [testResults, setTestResults] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    const [currentTest, setCurrentTest] = useState('');
    const history = useHistory();
    const userInfo = useSelector((state) => state.login.userInfo);

    // Check if user has admin role, redirect if not
    useEffect(() => {
        if (userInfo && userInfo.role && userInfo.role.name !== 'ADMIN') {
            history.push('/home');
        }
    }, [userInfo, history]);

    // Don't render if user is not admin
    if (!userInfo || userInfo.role?.name !== 'ADMIN') {
        return null;
    }

    // Define test configurations
    const testConfigs = [
        {
            name: 'Basic Query - No Filters',
            config: {
                page: 1,
                size: 10,
                orderBy: [],
                searchTags: {}
            }
        },
        {
            name: 'Large Page Size - No Filters',
            config: {
                page: 1,
                size: 100,
                orderBy: [],
                searchTags: {}
            }
        },
        {
            name: 'With Date Range Filter',
            config: {
                page: 1,
                size: 50,
                orderBy: [],
                searchTags: {
                    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
                    dateTo: new Date().toISOString().split('T')[0] // today
                }
            }
        },
        {
            name: 'With Multiple Filters',
            config: {
                page: 1,
                size: 25,
                orderBy: [{ param: 'createdDate', ord: 'desc' }],
                searchTags: {
                    dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
                    dateTo: new Date().toISOString().split('T')[0],
                    affiliateName: 'test',
                    planName: 'test'
                }
            }
        },
        {
            name: 'Multiple Pages Test',
            config: {
                page: 5,
                size: 20,
                orderBy: [{ param: 'createdDate', ord: 'asc' }],
                searchTags: {}
            }
        },
        {
            name: 'Today\'s Attentions - Empty Results',
            config: {
                page: 1,
                size: 50,
                orderBy: [{ param: 'createdDate', ord: 'desc' }],
                searchTags: {
                    dateFrom: new Date().toISOString().split('T')[0], // today
                    dateTo: new Date().toISOString().split('T')[0] // today
                }
            }
        },
        {
            name: 'With Provider Filter',
            config: {
                page: 1,
                size: 25,
                orderBy: [{ param: 'createdDate', ord: 'desc' }],
                searchTags: {
                    providerName: 'test'
                }
            }
        },
        {
            name: 'With Medical Service Filter',
            config: {
                page: 1,
                size: 25,
                orderBy: [{ param: 'createdDate', ord: 'desc' }],
                searchTags: {
                    medicalServiceName: 'test'
                }
            }
        }
    ];

    const runPerformanceTest = async () => {
        setIsRunning(true);
        setTestResults([]);
        
        const results = [];
        
        for (const testConfig of testConfigs) {
            setCurrentTest(`${testConfig.name} - Running 5 iterations...`);
            
            const durations = [];
            const errors = [];
            
            // Run each test 5 times
            for (let i = 1; i <= 5; i++) {
                setCurrentTest(`${testConfig.name} - Iteration ${i}/5`);
                
                const startTime = performance.now();
                try {
                    await services.getAllAttentions(testConfig.config);
                    const endTime = performance.now();
                    const duration = endTime - startTime;
                    durations.push(duration);
                } catch (error) {
                    const endTime = performance.now();
                    const duration = endTime - startTime;
                    durations.push(duration);
                    errors.push(error.message || 'Unknown error');
                }
                
                // Small delay between iterations to avoid overwhelming the server
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            // Calculate average duration
            const averageDuration = durations.reduce((sum, duration) => sum + duration, 0) / durations.length;
            const successCount = durations.length - errors.length;
            const errorCount = errors.length;
            
            results.push({
                name: testConfig.name,
                duration: averageDuration.toFixed(2),
                status: errorCount === 0 ? 'Success' : errorCount === 5 ? 'Error' : 'Partial Success',
                config: testConfig.config,
                timestamp: new Date().toLocaleTimeString(),
                iterations: 5,
                successCount,
                errorCount,
                allDurations: durations.map(d => d.toFixed(2)),
                errors: errors.length > 0 ? errors : undefined
            });
            
            setTestResults([...results]);
            
            // Small delay between different test configurations
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        setIsRunning(false);
        setCurrentTest('');
    };

    const runSingleTest = async (testConfig) => {
        setCurrentTest(`${testConfig.name} - Running 5 iterations...`);
        
        const durations = [];
        const errors = [];
        
        // Run the test 5 times
        for (let i = 1; i <= 5; i++) {
            setCurrentTest(`${testConfig.name} - Iteration ${i}/5`);
            
            const startTime = performance.now();
            try {
                await services.getAllAttentions(testConfig.config);
                const endTime = performance.now();
                const duration = endTime - startTime;
                durations.push(duration);
            } catch (error) {
                const endTime = performance.now();
                const duration = endTime - startTime;
                durations.push(duration);
                errors.push(error.message || 'Unknown error');
            }
            
            // Small delay between iterations
            await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // Calculate average duration
        const averageDuration = durations.reduce((sum, duration) => sum + duration, 0) / durations.length;
        const successCount = durations.length - errors.length;
        const errorCount = errors.length;
        
        const newResult = {
            name: testConfig.name,
            duration: averageDuration.toFixed(2),
            status: errorCount === 0 ? 'Success' : errorCount === 5 ? 'Error' : 'Partial Success',
            config: testConfig.config,
            timestamp: new Date().toLocaleTimeString(),
            iterations: 5,
            successCount,
            errorCount,
            allDurations: durations.map(d => d.toFixed(2)),
            errors: errors.length > 0 ? errors : undefined
        };
        
        setTestResults(prev => [...prev, newResult]);
        setCurrentTest('');
    };

    const clearResults = () => {
        setTestResults([]);
    };

    const getAverageResponseTime = () => {
        if (testResults.length === 0) return 0;
        const successfulTests = testResults.filter(result => result.status === 'Success');
        if (successfulTests.length === 0) return 0;
        
        const totalTime = successfulTests.reduce((sum, result) => sum + parseFloat(result.duration), 0);
        return (totalTime / successfulTests.length).toFixed(2);
    };

    const getSuccessRate = () => {
        if (testResults.length === 0) return 0;
        const successfulTests = testResults.filter(result => result.status === 'Success');
        return ((successfulTests.length / testResults.length) * 100).toFixed(1);
    };

    return (
        <div className={showSidebar && window.innerWidth <= 700 ? 'performance-page bg-mt-10' : 'performance-page'}>
            <div className="performance-header">
                <h1>Performance Testing - Attentions API</h1>
                <p>Test various API configurations and measure response times</p>
            </div>

            <div className="performance-controls">
                <button 
                    className="run-all-tests-btn"
                    onClick={runPerformanceTest}
                    disabled={isRunning}
                >
                    {isRunning ? 'Running Tests...' : 'Run All Tests'}
                </button>
                
                <button 
                    className="clear-results-btn"
                    onClick={clearResults}
                    disabled={isRunning}
                >
                    Clear Results
                </button>
            </div>

            {isRunning && (
                <div className="test-progress">
                    <p>Currently testing: <strong>{currentTest}</strong></p>
                    <div className="progress-bar">
                        <div 
                            className="progress-fill"
                            style={{ width: `${(testResults.length / testConfigs.length) * 100}%` }}
                        ></div>
                    </div>
                </div>
            )}

            {testResults.length > 0 && (
                <div className="performance-summary">
                    <h3>Test Summary</h3>
                    <div className="summary-stats">
                        <div className="stat">
                            <span className="stat-label">Total Tests:</span>
                            <span className="stat-value">{testResults.length}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Success Rate:</span>
                            <span className="stat-value">{getSuccessRate()}%</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Average Response Time:</span>
                            <span className="stat-value">{getAverageResponseTime()}ms</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="test-configs">
                <h3>Test Configurations</h3>
                <div className="config-grid">
                    {testConfigs.map((testConfig, index) => (
                        <div key={index} className="config-card">
                            <h4>{testConfig.name}</h4>
                            <div className="config-details">
                                <p><strong>Page:</strong> {testConfig.config.page}</p>
                                <p><strong>Size:</strong> {testConfig.config.size}</p>
                                {testConfig.config.searchTags.dateFrom && (
                                    <p><strong>Date Range:</strong> {testConfig.config.searchTags.dateFrom} to {testConfig.config.searchTags.dateTo}</p>
                                )}
                                {testConfig.config.orderBy.length > 0 && (
                                    <p><strong>Sort:</strong> {testConfig.config.orderBy.map(ord => `${ord.param}:${ord.ord}`).join(', ')}</p>
                                )}
                                {Object.keys(testConfig.config.searchTags).length > 0 && (
                                    <p><strong>Filters:</strong> {Object.entries(testConfig.config.searchTags).map(([key, value]) => `${key}: ${value}`).join(', ')}</p>
                                )}
                            </div>
                            <button 
                                className="run-single-test-btn"
                                onClick={() => runSingleTest(testConfig)}
                                disabled={isRunning}
                            >
                                Run Test
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {testResults.length > 0 && (
                <div className="test-results">
                    <h3>Test Results</h3>
                    <div className="results-table-container">
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th>Test Name</th>
                                    <th>Avg Duration (ms)</th>
                                    <th>Status</th>
                                    <th>Success Rate</th>
                                    <th>Timestamp</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testResults.map((result, index) => (
                                    <tr key={index} className={result.status === 'Error' ? 'error-row' : result.status === 'Partial Success' ? 'partial-row' : 'success-row'}>
                                        <td>{result.name}</td>
                                        <td>
                                            <div className="duration-info">
                                                <strong>{result.duration}</strong>
                                                <small>avg of {result.iterations} runs</small>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${result.status.toLowerCase().replace(' ', '-')}`}>
                                                {result.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="success-rate">
                                                <span className="success-count">{result.successCount}/{result.iterations}</span>
                                                <small>{(result.successCount / result.iterations * 100).toFixed(0)}%</small>
                                            </div>
                                        </td>
                                        <td>{result.timestamp}</td>
                                        <td>
                                            <details>
                                                <summary>View Details</summary>
                                                <div className="test-details">
                                                    <h5>Configuration:</h5>
                                                    <pre>{JSON.stringify(result.config, null, 2)}</pre>
                                                    
                                                    <h5>Individual Run Times:</h5>
                                                    <div className="durations-list">
                                                        {result.allDurations.map((duration, i) => (
                                                            <span key={i} className="duration-item">
                                                                Run {i + 1}: {duration}ms
                                                            </span>
                                                        ))}
                                                    </div>
                                                    
                                                    {result.errors && result.errors.length > 0 && (
                                                        <div className="error-details">
                                                            <h5>Errors:</h5>
                                                            {result.errors.map((error, i) => (
                                                                <div key={i} className="error-item">
                                                                    <strong>Run {i + 1}:</strong> {error}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </details>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    showSidebar: state.common.showSidebar,
});

export default connect(mapStateToProps)(Performance);
